import React, { useState, useEffect } from 'react';
import Dashboard from './pages/Dashboard';
import ProviderCourses from './pages/ProviderCourses';
import ProviderOrders from './pages/ProviderOrders';
import BasicInformation from './pages/BasicInformation';
import Followers from './pages/Followers';
import ProviderTransactions from './pages/ProviderTransactions';
import ProviderSidebar from './ProviderSidebar';
import { Menu, LogOut, User } from 'lucide-react';
import { API_BASE_URL, IMAGE_BASE_URL } from '../config';

const ProviderPanel = ({ authToken, onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [headerAvatarError, setHeaderAvatarError] = useState(false);

  const fetchProfile = async (updatedData) => {
    if (updatedData && typeof updatedData === 'object') {
      const flat = updatedData.profile || updatedData.data || updatedData;
      const payload = flat && typeof flat === 'object' ? flat : updatedData;
      setProfile(prev => (prev ? { ...prev, ...payload } : payload));
      setHeaderAvatarError(false);
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/admin/profile`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setHeaderAvatarError(false);
      }
    } catch (error) {
      console.error("Failed to fetch profile for header:", error);
    }
  };

  const headerAvatarUrl = profile?.dp_image
    ? (profile.dp_image.startsWith('http') ? profile.dp_image : `${IMAGE_BASE_URL}${profile.dp_image.replace(/^\//, '')}`)
    : null;
  const showHeaderAvatar = headerAvatarUrl && !headerAvatarError;

  useEffect(() => {
    fetchProfile();
  }, [authToken]);

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard authToken={authToken} setActiveTab={setActiveTab} />;
      case 'basic-info':
        return <BasicInformation authToken={authToken} onProfileUpdate={fetchProfile} />;
      case 'courses':
        return <ProviderCourses authToken={authToken} />;
      case 'orders':
        return <ProviderOrders authToken={authToken} />;
      case 'followers':
        return <Followers authToken={authToken} />;
      case 'transactions':
        return <ProviderTransactions authToken={authToken} />;
      default:
        return <Dashboard authToken={authToken} />;
    }
  };

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProviderSidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md hover:bg-gray-100"
              >
                <Menu className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-bold text-gray-900">Provider Panel</h1>
              <span className="flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                <User className="h-3 w-3 mr-1" /> Provider
              </span>
            </div>

            <div className="flex items-center space-x-4">
              {profile && (
                <div className="hidden md:flex items-center space-x-3 mr-4">
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">{profile.first_name || profile.name || 'Provider'}</p>
                    <p className="text-xs text-gray-500 capitalize">{profile.artist_type || 'Artist'}</p>
                  </div>
                  <div className="h-8 w-8 rounded-full bg-gray-200 overflow-hidden border border-gray-100 flex items-center justify-center">
                    {showHeaderAvatar ? (
                      <img
                        src={headerAvatarUrl}
                        alt="Avatar"
                        className="w-full h-full object-cover"
                        key={profile.dp_image}
                        onError={() => setHeaderAvatarError(true)}
                      />
                    ) : (
                      <User className="w-full h-full p-2 text-gray-400" />
                    )}
                  </div>
                </div>
              )}

              <button
                onClick={() => {
                  if (window.confirm('Logout?')) onLogout();
                }}
                className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                title="Logout"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:block">Logout</span>
              </button>
            </div>
          </div>
        </header>
        <main className="flex-1 p-6 overflow-y-auto">{renderContent()}</main>
      </div>
    </div>
  );
};

export default ProviderPanel;
